import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import type { AppSx } from "../types";

export interface DecorationBindingComponentStyles {
  root: AppSx;
  emptyAlert: AppSx;
  fieldCard: AppSx;
  fieldHeaderRow: AppSx;
  fieldHeaderRadioCol: AppSx;
  fieldTitle: AppSx;
  fieldGrid: AppSx;
  radioFormControl: AppSx;
  previewSection: AppSx;
  previewLabel: AppSx;
  previewValue: AppSx;
  pathOptionLabel: AppSx;
  pathSample: AppSx;
  tableSection: AppSx;
  columnCard: AppSx;
  columnTitle: AppSx;
}

export function buildDecorationBindingComponentStyles(
  theme: Theme
): DecorationBindingComponentStyles {
  return {
    root: {
      display: "flex",
      flexDirection: "column",
      gap: 1,
      minWidth: { sm: 360 },
    },
    emptyAlert: {
      py: 0.25,
      "& .MuiAlert-message": {
        py: 0.25,
      },
    },
    fieldCard: {
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      px: { xs: 1, sm: 1.25 },
      py: 0.75,
      bgcolor: alpha(theme.palette.background.default, 0.2),
    },
    fieldHeaderRow: {
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: "minmax(100px, 30%) minmax(260px, 1fr)",
      },
      columnGap: { xs: 0.5, sm: 2 },
      rowGap: 0.5,
      alignItems: "center",
      mb: 0.5,
    },
    fieldHeaderRadioCol: {
      minWidth: 0,
      width: "100%",
    },
    fieldTitle: {
      fontWeight: 600,
      fontSize: "0.8125rem",
      lineHeight: 1.3,
      color: "text.primary",
      minWidth: 0,
    },
    fieldGrid: {
      mt: 0.25,
    },
    radioFormControl: {
      margin: 0,
      width: "100%",
      "& .MuiFormLabel-root": {
        display: "none",
      },
      "& .MuiFormGroup-root": {
        flexDirection: "row",
        flexWrap: { xs: "wrap", sm: "nowrap" },
        justifyContent: "flex-start",
        columnGap: 1.5,
        rowGap: 0.25,
        width: "100%",
      },
      "& .MuiFormControlLabel-root": {
        marginRight: 0,
        marginLeft: 0,
        whiteSpace: "nowrap",
      },
      "& .MuiFormControlLabel-label": {
        fontSize: "0.8125rem",
      },
    },
    previewSection: {
      display: "flex",
      alignItems: "baseline",
      gap: 1,
      mt: 0.25,
    },
    previewLabel: {
      flexShrink: 0,
      fontSize: "0.75rem",
      color: "text.secondary",
    },
    previewValue: {
      flex: 1,
      minWidth: 0,
      px: 0.75,
      py: 0.375,
      borderRadius: 0.75,
      bgcolor: "background.paper",
      border: "1px dashed",
      borderColor: "divider",
      fontFamily: "monospace",
      fontSize: "0.75rem",
      lineHeight: 1.35,
      wordBreak: "break-word",
    },
    pathOptionLabel: {
      display: "flex",
      flexDirection: "column",
      gap: 0.125,
      py: 0.25,
    },
    pathSample: {
      fontSize: "0.6875rem",
      color: "text.secondary",
    },
    tableSection: {
      display: "flex",
      flexDirection: "column",
      gap: 1,
      mt: 0.5,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      px: { xs: 1, sm: 1.25 },
      py: 0.75,
      bgcolor: alpha(theme.palette.background.default, 0.2),
    },
    columnCard: {
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      px: 1,
      py: 0.75,
      bgcolor: alpha(theme.palette.background.paper, 0.35),
    },
    columnTitle: {
      fontWeight: 600,
      fontSize: "0.75rem",
      color: "text.secondary",
      mb: 0.5,
    },
  };
}
